import axios from 'axios';
import { API_CONFIG } from '../constants/index.js';

/**
 * Withdrawal Service
 * Handles all withdrawal-related API operations
 * Uses the global axios interceptors from AuthService for authentication
 */
class WithdrawalService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.withdrawalsEndpoint = '/api/withdrawals';
  }

  /**
   * Request a withdrawal
   * @param {object} withdrawalData - Withdrawal request data
   * @returns {Promise<object>} API response
   */
  async requestWithdrawal(withdrawalData) {
    try {
      const response = await axios.post(`${this.baseURL}${this.withdrawalsEndpoint}/request`, withdrawalData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get available balance for a project
   * @param {string} projectId - Project ID
   * @param {string} currency - Currency code
   * @returns {Promise<object>} Available balance data
   */
  async getAvailableBalance(projectId, currency = 'Pi') {
    try {
      const response = await axios.get(`${this.baseURL}${this.withdrawalsEndpoint}/balance/${projectId}`, {
        params: { currency }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all currency balances for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<object>} All currency balances
   */
  async getAllBalances(projectId) {
    try {
      const response = await axios.get(`${this.baseURL}${this.withdrawalsEndpoint}/balances/${projectId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get list of supported Nigerian banks
   * @returns {Promise<object>} List of banks
   */
  async getBanks() {
    try {
      const response = await axios.get(`${this.baseURL}${this.withdrawalsEndpoint}/banks`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Resolve bank account name
   * @param {string} accountNumber - 10-digit account number
   * @param {string} bankCode - Bank code
   * @returns {Promise<object>} Resolved account details
   */
  async resolveAccount(accountNumber, bankCode) {
    try {
      const response = await axios.get(`${this.baseURL}${this.withdrawalsEndpoint}/resolve-account`, {
        params: { accountNumber, bankCode }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get withdrawal history for a project
   * @param {string} projectId - Project ID
   * @param {object} options - Query options
   * @returns {Promise<object>} Withdrawal history
   */
  async getProjectWithdrawals(projectId, options = {}) {
    try {
      const { page = 1, limit = 20 } = options;
      const response = await axios.get(`${this.baseURL}${this.withdrawalsEndpoint}/history/${projectId}`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user's withdrawals across all projects
   * @param {string} userId - User ID
   * @param {object} options - Query options
   * @returns {Promise<object>} User withdrawals
   */
  async getUserWithdrawals(userId, options = {}) {
    try {
      const { page = 1, limit = 20 } = options;
      const response = await axios.get(`${this.baseURL}${this.withdrawalsEndpoint}/user/${userId}`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Validate Pi wallet address format
   * @param {string} walletAddress - Wallet address to validate
   * @returns {object} Validation result
   */
  validateWalletAddress(walletAddress) {
    if (!walletAddress) {
      return { valid: false, error: 'Wallet address is required' };
    }

    if (typeof walletAddress !== 'string') {
      return { valid: false, error: 'Wallet address must be a string' };
    }

    const trimmed = walletAddress.trim();

    if (!trimmed.startsWith('G')) {
      return { valid: false, error: 'Pi wallet address must start with "G"' };
    }

    if (trimmed.length !== 56) {
      return { valid: false, error: 'Pi wallet address must be exactly 56 characters long' };
    }

    // Basic base32 character validation
    const base32Regex = /^[A-Z2-7]+$/;
    if (!base32Regex.test(trimmed)) {
      return { valid: false, error: 'Pi wallet address contains invalid characters' };
    }

    return { valid: true };
  }

  /**
   * Validate withdrawal amount
   * @param {number|string} amount - Amount to validate
   * @param {string} currency - Currency code
   * @returns {object} Validation result
   */
  validateWithdrawalAmount(amount, currency = 'PI') {
    const numAmount = parseFloat(amount);
    const isNGN = currency.toUpperCase() === 'NGN';
    const symbol = isNGN ? '₦' : 'π';

    if (isNaN(numAmount)) {
      return { valid: false, error: 'Invalid amount format' };
    }

    if (numAmount <= 0) {
      return { valid: false, error: 'Amount must be greater than 0' };
    }

    // Minimum amount: 10 Pi or 100 NGN
    const minAmount = isNGN ? 100 : 10;
    if (numAmount < minAmount) {
      return { valid: false, error: `Minimum withdrawal amount is ${minAmount} ${symbol}` };
    }

    // Maximum amount: 1,000,000 Pi or 5,000,000 NGN
    const maxAmount = isNGN ? 5000000 : 1000000;
    if (numAmount > maxAmount) {
      return { valid: false, error: `Maximum withdrawal amount is ${maxAmount.toLocaleString()} ${symbol}` };
    }

    // Check for reasonable decimal places (max 2)
    if (numAmount.toString().includes('.') && numAmount.toString().split('.')[1].length > 2) {
      return { valid: false, error: 'Amount can have maximum 2 decimal places' };
    }

    return { valid: true };
  }

  /**
   * Format withdrawal status for display
   * @param {string} status - Withdrawal status
   * @param {string} currency - Currency code
   * @returns {object} Formatted status
   */
  formatStatus(status, currency = 'PI') {
    const isNGN = currency === 'NGN';

    const statusMap = {
      pending: {
        label: 'Pending Review',
        color: 'yellow',
        description: 'Waiting for admin approval'
      },
      approved: {
        label: 'Approved',
        color: 'blue',
        description: isNGN ? 'Approved for bank transfer' : 'Approved and queued for processing'
      },
      processing: {
        label: 'Processing',
        color: 'blue',
        description: isNGN ? 'Processing bank transfer' : 'Being processed on Pi blockchain'
      },
      submitted: {
        label: 'Submitted',
        color: 'blue',
        description: isNGN ? 'Submitted to bank' : 'Submitted to Pi blockchain'
      },
      completed: {
        label: 'Completed',
        color: 'green',
        description: 'Successfully completed'
      },
      rejected: {
        label: 'Rejected',
        color: 'red',
        description: 'Rejected by admin'
      },
      failed: {
        label: 'Failed',
        color: 'red',
        description: 'Processing failed'
      }
    };

    return statusMap[status] || {
      label: status,
      color: 'gray',
      description: 'Unknown status'
    };
  }

  /**
   * Format currency amount for display
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (PI or NGN)
   * @returns {string} Formatted amount
   */
  formatAmount(amount, currency = 'PI') {
    // Import getCurrencySymbol from constants
    const SUPPORTED_CURRENCIES = {
      PI: { symbol: 'π' },
      NGN: { symbol: '₦' }
    };

    const symbol = SUPPORTED_CURRENCIES[currency]?.symbol || 'π';

    if (!amount) return `0 ${symbol}`;

    const numAmount = parseFloat(amount);

    // Format with appropriate decimal places
    if (numAmount >= 1000000) {
      return `${(numAmount / 1000000).toFixed(1)}M ${symbol}`;
    } else if (numAmount >= 1000) {
      return `${(numAmount / 1000).toFixed(1)}K ${symbol}`;
    } else if (numAmount % 1 === 0) {
      return `${numAmount.toLocaleString()} ${symbol}`;
    } else {
      return `${numAmount.toFixed(2)} ${symbol}`;
    }
  }

  /**
   * Get withdrawal status badge color class
   * @param {string} status - Withdrawal status
   * @returns {string} CSS class for status badge
   */
  getStatusBadgeClass(status) {
    const statusInfo = this.formatStatus(status);
    const colorMap = {
      green: 'bg-green-100 text-green-800 border-green-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return colorMap[statusInfo.color] || colorMap.gray;
  }

  /**
   * Calculate estimated processing time
   * @param {string} status - Current withdrawal status
   * @param {number} amount - Withdrawal amount
   * @param {string} currency - Currency code
   * @returns {string} Estimated time
   */
  getEstimatedProcessingTime(status, amount, currency = 'PI') {
    if (status === 'completed') return 'Completed';
    if (status === 'rejected' || status === 'failed') return 'N/A';

    const isNGN = currency === 'NGN';
    const isAutoApproved = (!isNGN && amount <= 10000) || (isNGN && amount <= 500000);

    if (isAutoApproved) {
      // Auto-approved withdrawals
      switch (status) {
        case 'approved':
        case 'processing':
          return '5-15 minutes';
        case 'submitted':
          return '2-5 minutes';
        default:
          return '5-15 minutes';
      }
    } else {
      // Manual approval required
      switch (status) {
        case 'pending':
          return '1-3 business days';
        case 'approved':
        case 'processing':
          return '5-15 minutes';
        case 'submitted':
          return '2-5 minutes';
        default:
          return '1-3 business days';
      }
    }
  }

  /**
   * Check if withdrawal can be cancelled
   * @param {string} status - Withdrawal status
   * @returns {boolean} Whether withdrawal can be cancelled
   */
  canCancelWithdrawal(status) {
    return ['pending'].includes(status);
  }

  /**
   * Check if withdrawal is in progress
   * @param {string} status - Withdrawal status
   * @returns {boolean} Whether withdrawal is in progress
   */
  isWithdrawalInProgress(status) {
    return ['approved', 'processing', 'submitted'].includes(status);
  }

  /**
   * Check if withdrawal is final (completed or failed)
   * @param {string} status - Withdrawal status
   * @returns {boolean} Whether withdrawal is in final state
   */
  isWithdrawalFinal(status) {
    return ['completed', 'rejected', 'failed'].includes(status);
  }

  /**
   * Get withdrawal icon based on status
   * @param {string} status - Withdrawal status
   * @returns {string} Icon name
   */
  getStatusIcon(status) {
    const iconMap = {
      pending: 'clock',
      approved: 'check-circle',
      processing: 'refresh-cw',
      submitted: 'send',
      completed: 'check-circle-2',
      rejected: 'x-circle',
      failed: 'alert-circle'
    };

    return iconMap[status] || 'help-circle';
  }

  /**
   * Handle API errors consistently
   * @param {object} error - Axios error object
   * @returns {object} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { data, status } = error.response;
      return {
        message: data?.error?.message || 'An error occurred',
        code: data?.error?.code || 'UNKNOWN_ERROR',
        status,
        details: data?.error?.details
      };
    } else if (error.request) {
      // Network error
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        status: 0
      };
    } else {
      // Other error
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 0
      };
    }
  }
}

// Create and export singleton instance
const withdrawalService = new WithdrawalService();
export default withdrawalService;
